import {useState} from 'react'
import {X, Lock, Globe, Loader2, LayoutPanelTop} from 'lucide-react'
import Modal from '../common/Modal'
import ConfirmDialog from '../common/ConfirmDialog'
import type {Board, BoardVisibility} from '../../types'
import {useApp} from '../../context/AppContext'

interface BoardFormProps {
  onClose: () => void
  board?: Board
}

export default function BoardForm({onClose, board}: BoardFormProps) {
  const {state, apiCreateBoard, apiUpdateBoard} = useApp()
  const isEditing = !!board

  const [name, setName] = useState(board?.name || '')
  const [description, setDescription] = useState(board?.description || '')
  const [teamId, setTeamId] = useState(board?.teamId || state.teams[0]?.id || '')
  const [visibility, setVisibility] = useState<BoardVisibility>(board?.visibility || 'public')
  const [allowedUserIds, setAllowedUserIds] = useState<string[]>(board?.allowedUserIds || [])
  const [allowedTeamIds, setAllowedTeamIds] = useState<string[]>(board?.allowedTeamIds || [])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const isDirty =
    name !== (board?.name || '') ||
    description !== (board?.description || '') ||
    teamId !== (board?.teamId || state.teams[0]?.id || '') ||
    visibility !== (board?.visibility || 'public') ||
    JSON.stringify(allowedUserIds) !== JSON.stringify(board?.allowedUserIds || []) ||
    JSON.stringify(allowedTeamIds) !== JSON.stringify(board?.allowedTeamIds || [])
  const requestClose = () => {
    if (submitting) return
    if (isDirty) setShowDiscardConfirm(true)
    else onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || submitting) return

    setSubmitting(true)
    setSubmitError('')
    try {
      if (isEditing) {
        await apiUpdateBoard({
          id: board.id,
          name: name.trim(),
          description: description.trim(),
          teamId,
          visibility,
          allowedUserIds: visibility === 'private' ? allowedUserIds : [],
          allowedTeamIds: visibility === 'private' ? allowedTeamIds : [],
        })
      } else {
        await apiCreateBoard({
          name: name.trim(),
          description: description.trim(),
          teamId,
          visibility,
          allowedUserIds: visibility === 'private' ? allowedUserIds : [],
          allowedTeamIds: visibility === 'private' ? allowedTeamIds : [],
          columns: [
            {id: '', title: 'Backlog', status: 'backlog', color: '#94a3b8'},
            {id: '', title: 'A Fazer', status: 'todo', color: '#3b82f6'},
            {id: '', title: 'Em Progresso', status: 'in_progress', color: '#3570E6', limit: 5},
            {id: '', title: 'Em Revisão', status: 'in_review', color: '#f59e0b', limit: 3},
            {id: '', title: 'Concluído', status: 'done', color: '#10b981'},
          ],
        })
      }
      onClose()
    } catch (error) {
      setSubmitError(
        'Não foi possível salvar o quadro. Seus dados foram mantidos; tente novamente.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal
        isOpen
        onClose={requestClose}
        closeDisabled={submitting}
        title={isEditing ? 'Editar quadro' : 'Novo quadro'}
        description='Organize o trabalho, defina a equipe e controle quem pode acessar.'
        size='lg'
      >
        <form onSubmit={handleSubmit} className='p-5 sm:p-6' aria-busy={submitting}>
          <fieldset disabled={submitting} className='m-0 min-w-0 border-0 p-0'>
            <div className='space-y-4'>
              <div>
                <label
                  htmlFor='board-name'
                  className='block text-sm font-medium text-slate-700 mb-1'
                >
                  Nome do quadro *
                </label>
                <input
                  id='board-name'
                  type='text'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='Nome do quadro...'
                  className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all'
                  autoFocus
                  data-dialog-autofocus
                  required
                  maxLength={150}
                />
              </div>

              <div>
                <label
                  htmlFor='board-desc'
                  className='block text-sm font-medium text-slate-700 mb-1'
                >
                  Descrição
                </label>
                <textarea
                  id='board-desc'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='Descrição do quadro...'
                  rows={2}
                  className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all resize-none'
                />
              </div>

              <div>
                <label
                  htmlFor='board-team'
                  className='block text-sm font-medium text-slate-700 mb-1'
                >
                  Equipe
                </label>
                <select
                  id='board-team'
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 bg-white'
                >
                  <option value=''>Sem equipe</option>
                  {state.teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>
                  Visibilidade
                </label>
                <div className='flex gap-3'>
                  <button
                    type='button'
                    onClick={() => setVisibility('public')}
                    aria-pressed={visibility === 'public'}
                    className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                      visibility === 'public'
                        ? 'border-brand-400 bg-brand-50 text-brand-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Globe size={18} />
                    <div className='text-left'>
                      <p className='font-medium'>Público</p>
                      <p className='text-xs text-slate-500 font-normal'>Visível para todos</p>
                    </div>
                  </button>
                  <button
                    type='button'
                    onClick={() => setVisibility('private')}
                    aria-pressed={visibility === 'private'}
                    className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                      visibility === 'private'
                        ? 'border-brand-400 bg-brand-50 text-brand-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Lock size={18} />
                    <div className='text-left'>
                      <p className='font-medium'>Privado</p>
                      <p className='text-xs text-slate-500 font-normal'>Acesso restrito</p>
                    </div>
                  </button>
                </div>
              </div>

              {visibility === 'private' && (
                <>
                  <p className='rounded-xl bg-brand-50 p-3 text-xs leading-relaxed text-brand-700'>
                    Selecione as equipes e pessoas que precisam acessar este quadro privado.
                  </p>
                  <div>
                    <label className='block text-sm font-medium text-slate-700 mb-1'>
                      Equipes com acesso
                    </label>
                    <div className='flex flex-wrap gap-1.5 mb-2'>
                      {allowedTeamIds.map((tid) => {
                        const t = state.teams.find((tm) => tm.id === tid)
                        return t ? (
                          <span
                            key={tid}
                            className='inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border'
                            style={{
                              borderColor: `${t.color}40`,
                              backgroundColor: `${t.color}10`,
                              color: t.color,
                            }}
                          >
                            {t.name}
                            <button
                              type='button'
                              onClick={() =>
                                setAllowedTeamIds(allowedTeamIds.filter((id) => id !== tid))
                              }
                              aria-label={`Remover acesso da equipe ${t.name}`}
                              className='hover:opacity-70'
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ) : null
                      })}
                    </div>
                    <select
                      aria-label='Adicionar acesso ao quadro'
                      onChange={(e) => {
                        if (e.target.value && !allowedTeamIds.includes(e.target.value)) {
                          setAllowedTeamIds([...allowedTeamIds, e.target.value])
                        }
                        e.target.value = ''
                      }}
                      defaultValue=''
                      className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300'
                    >
                      <option value='' disabled>
                        Adicionar equipe...
                      </option>
                      {state.teams
                        .filter((t) => !allowedTeamIds.includes(t.id))
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-slate-700 mb-1'>
                      Usuários com acesso
                    </label>
                    <div className='flex flex-wrap gap-1.5 mb-2'>
                      {allowedUserIds.map((uid) => {
                        const u = state.users.find((usr) => usr.id === uid)
                        return u ? (
                          <span
                            key={uid}
                            className='inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-xs font-medium text-slate-600'
                          >
                            {u.name}
                            <button
                              type='button'
                              onClick={() =>
                                setAllowedUserIds(allowedUserIds.filter((id) => id !== uid))
                              }
                              aria-label={`Remover acesso de ${u.name}`}
                              className='text-slate-400 hover:text-slate-600'
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ) : null
                      })}
                    </div>
                    <select
                      aria-label='Adicionar acesso ao quadro'
                      onChange={(e) => {
                        if (e.target.value && !allowedUserIds.includes(e.target.value)) {
                          setAllowedUserIds([...allowedUserIds, e.target.value])
                        }
                        e.target.value = ''
                      }}
                      defaultValue=''
                      className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300'
                    >
                      <option value='' disabled>
                        Adicionar usuário...
                      </option>
                      {state.users
                        .filter((u) => !allowedUserIds.includes(u.id))
                        .map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {!isEditing && (
              <div className='mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4'>
                <p className='flex items-center gap-2 text-sm font-semibold text-slate-700'>
                  <LayoutPanelTop size={16} /> Fluxo inicial do quadro
                </p>
                <p className='m-0 text-xs leading-relaxed text-slate-500'>
                  Backlog → A fazer → Em progresso → Em revisão → Concluído
                </p>
                <p className='m-0 mt-1 text-xs text-slate-400'>
                  As colunas podem ser personalizadas após a criação.
                </p>
              </div>
            )}
          </fieldset>
          {submitError && (
            <p role='alert' className='mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700'>
              {submitError}
            </p>
          )}
          <div className='flex gap-3 mt-6 border-t border-slate-100 pt-5'>
            <button
              type='button'
              onClick={requestClose}
              disabled={submitting}
              className='flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors'
            >
              Cancelar
            </button>
            <button
              type='submit'
              disabled={!name.trim() || submitting}
              className='devops-primary-button flex-1 disabled:opacity-40 disabled:cursor-not-allowed'
            >
              {submitting && <Loader2 size={16} className='animate-spin' />}
              {submitting
                ? isEditing
                  ? 'Salvando...'
                  : 'Criando...'
                : isEditing
                ? 'Salvar alterações'
                : 'Criar Quadro'}
            </button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={showDiscardConfirm}
        title='Descartar alterações do quadro'
        message='Os dados preenchidos ainda não foram salvos. Deseja descartá-los?'
        confirmLabel='Descartar'
        onCancel={() => setShowDiscardConfirm(false)}
        onConfirm={onClose}
      />
    </>
  )
}
